import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import Navbar from "@components/Navbar/Navbar";
import { notify } from "@utils/notify";
import useFacebookPages from "@hooks/useFacebookPages";
import useAds from "@hooks/useAds";

import useFacebookAdCreatives from "@hooks/useFacebookAdCreatives";
import useFacebookAds from "@hooks/useFacebookAds";
import { useCreatives } from "@hooks/useCreatives";
import { useDuplicateCreatives } from "@hooks/useCreatives";
import { createMultiPlacementImageAdCreativePayload, createMultiPlacementVideoAdCreativePayload } from "../ReviewDefaultValues/helper";
import { createSingleVideoAdCreativePayload, createSingleImageAdCreativePayload } from "../ReviewDefaultValues/newAdCreateHelpers";
import { useSelector } from "react-redux";

const steps = [
  {
    id: 1,
    name: "Step 1: Validation",
    key: "validation",
    description: "Validating ad creatives and configurations"
  },
  {
    id: 2,
    name: "Step 2: Uploading and Updating",
    key: "upload",
    description: "Uploading media files to Facebook"
  },
  {
    id: 3,
    name: "Step 3: Draft Ad Creative",
    key: "creative",
    description: "Creating ad creatives on Facebook"
  },
  {
    id: 4,
    name: "Step 4: Publishing Ads",
    key: "publishing",
    description: "Publishing ads to Facebook"
  }
];

const truncateFileName = (fileName, maxLength = 30) => {
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.split('.').pop();
  const name = fileName.slice(0, maxLength - extension.length - 3);
  return `${name}...${extension}`;
};

export default function EventLog() {
  const location = useLocation();
  const reviewValues = location.state?.reviewValues || {};
  const [status, setStatus] = useState(Array(steps.length).fill("pending"));
  const [errorStep, setErrorStep] = useState(null);
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const { handleGetAdsWithCreatives } = useAds();
  const { handleCreateAdCreative, handlePostAd } = useFacebookAdCreatives();
  const { handleCreateFacebookAd } = useFacebookAds();
  const { handleUploadAdImage, handleUploadAdVideos, handleVideoThumbnailUrl, handleMultipleVideoThumbnailUrl } = useFacebookPages();
  const { handleUpdateCreative } = useCreatives();
  const { handleGetDuplicateCreativeUrls } = useDuplicateCreatives();

  const { ads } = useSelector((state) => state.ads) ?? {};
  const { defaultValues } = useSelector((state) => state.defaultValues) ?? {};
  const { adCopies } = useSelector((state) => state.adCopies) ?? {};
  const { user } = useSelector((state) => state.user);
  const { adAccountId } = user ?? {};

  const [stepDetails, setStepDetails] = useState({
    validation: { total: 0, current: 0, message: '' },
    upload: { total: 0, current: 0, message: '' },
    creative: { total: 0, current: 0, message: '' },
    publishing: { total: 0, current: 0, message: '' }
  });

  const [stepErrors, setStepErrors] = useState({
    validation: [],
    upload: [],
    creative: [],
    publishing: []
  });

  const updateStepProgress = (step, current, total, message) => {
    setStepDetails(prev => ({
      ...prev,
      [step]: {
        current,
        total,
        message
      }
    }));
  };

  const addStepError = (step, error) => {
    setStepErrors(prev => ({
      ...prev,
      [step]: [...new Set([...prev[step], error])] // Prevents duplicates
    }));
  };

  const handleSaveAndNext = async () => {
    // Step 1: Validation
    if (!ads) {
      addStepError('validation', "Ads data is not available");
      updateStepProgress("validation", 0, 1, "Failed: Ads data not found");
      setStatus(prev => {
        const newStatus = [...prev];
        newStatus[0] = "error";
        return newStatus;
      });
      setErrorStep(0);
      return;
    }

    updateStepProgress("validation", 0, ads.length, "Starting validation...");

    let validatedCount = 0;
    for (const ad of ads) {
      try {
        const hasImageCreatives = ad.creatives.some(
          (creative) => creative.fileType.startsWith("image/")
        );
        const hasVideoCreatives = ad.creatives.some(
          (creative) => creative.fileType.startsWith("video/")
        );

        if (!ad.landingPageUrl) {
          throw new Error(`Landing page URL missing`);
        }

        if (hasImageCreatives && hasVideoCreatives) {
          throw new Error(`Cannot mix image and video creatives`);
        }

        validatedCount++;
        updateStepProgress(
          "validation",
          validatedCount,
          ads.length,
          `Validated ${validatedCount}/${ads.length} ads`
        );
      } catch (error) {
        addStepError('validation', `Error in ad "${ad.name}": ${error.message}`);
        updateStepProgress(
          "validation",
          validatedCount,
          ads.length,
          `Failed at ${ad.name}`
        );
        setStatus(prev => {
          const newStatus = [...prev];
          newStatus[0] = "error";
          return newStatus;
        });
        setErrorStep(0);
        return;
      }
    }

    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[0] = "done";
      return newStatus;
    });

    // Step 2: Uploading and Updating
    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[1] = "in-progress";
      return newStatus;
    });

    let creativeMap = {};
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    const randomDelay = Math.floor(Math.random() * 2000) + 5000;

    // Calculate total creatives to upload
    const totalCreatives = ads.reduce((total, ad) => total + ad.creatives.length, 0);
    updateStepProgress("upload", 0, totalCreatives, "Starting media uploads...");

    let uploadedCount = 0;
    const creativeUpdates = [];

    for (const ad of ads) {
      for (const creative of ad.creatives) {
        try {
            if (creative.fileType.startsWith("image/") && !creative?.facebookImageHash) {
            updateStepProgress(
              "upload",
              uploadedCount,
              totalCreatives,
              `Uploading image: ${creative.fileName}`
            );

            const imageHash = await handleUploadAdImage(
              adAccountId,
              creative.previewLink,
              creative.fileName
            );
            creativeMap[creative._id] = imageHash;
            await delay(randomDelay);

            const resp = await handleUpdateCreative(creative._id, {
              facebookImageHash: imageHash
            });
            creativeUpdates.push(resp);

          } else if (creative.fileType.startsWith("video/") && !creative?.facebookVideoId) {
            updateStepProgress(
              "upload",
              uploadedCount,
              totalCreatives,
              `Uploading video: ${creative.fileName}`
            );

            const videoId = await handleUploadAdVideos(
              adAccountId,
              creative.previewLink,
              creative.fileName
            );
            creativeMap[creative._id] = videoId;
            await delay(randomDelay);

            const resp = await handleUpdateCreative(creative._id, {
              facebookVideoId: videoId
            });
            creativeUpdates.push(resp);
          }

          uploadedCount++;
          updateStepProgress(
            "upload",
            uploadedCount,
            totalCreatives,
            `Uploaded ${uploadedCount}/${totalCreatives} files`
          );

        } catch (error) {
          const fileName = truncateFileName(creative.fileName);
          addStepError('upload', `Failed to upload ${fileName}: ${error.message}`);
          updateStepProgress(
            "upload",
            uploadedCount,
            totalCreatives,
            `Failed at ${creative.fileName}`
          );
          setStatus((prev) => {
            const newStatus = [...prev];
            newStatus[1] = "error";
            return newStatus;
          });
          setErrorStep(1);
          return;
        }
      }
    }

    const creativeResults = await Promise.allSettled(creativeUpdates);
    const creativeErrors = creativeResults.filter(
      (result) => result.status === "rejected"
    );

    if (creativeErrors.length > 0) {
      creativeErrors.forEach((error, index) => {
        addStepError('upload', `Upload ${index + 1} failed: ${error.reason}`);
      });
      notify("Some creatives failed to upload. Please try again.", "error");
      setStatus((prev) => {
        const newStatus = [...prev];
        newStatus[1] = "error";
        return newStatus;
      });
      setErrorStep(1);
      return;
    }

    updateStepProgress(
      "upload",
      totalCreatives,
      totalCreatives,
      "All media files uploaded successfully"
    );

    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[1] = "done";
      return newStatus;
    });

    // Step 3: Draft Ad Creative
    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[2] = "in-progress";
      return newStatus;
    });

    await handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId);
    updateStepProgress("creative", 0, ads.length, "Starting to create ad creatives...");

    let adCreativeIdsMap = {};
    let createdCount = 0;

    const newAdCreativePromises = ads.map(async (ad) => {
      try {
        updateStepProgress(
          "creative",
          createdCount,
          ads.length,
          `Creating creative for ad: ${ad.name}`
        );

        const adCopiesForAd = adCopies.filter((copy) => copy.adId._id === ad._id);
        const bodies = adCopiesForAd.map((copy) => ({
          text: copy.description,
          adlabels: [{ name: "body_copy_label" }],
        }));

        const titles = adCopiesForAd.map((copy) => ({
          text: copy.headline,
          adlabels: [{ name: "title_copy_label" }],
        }));

        const name = ad.name;
        const page_id = reviewValues[ad._id]?.facebookPage?.id;
        const instagram_actor_id = reviewValues[ad._id]?.instagramPage?.id;
        const callToActionLink = ad?.landingPageUrl;
        const callToActionType = reviewValues[ad._id]?.callToAction?.id;
        const trackingTemplate = reviewValues?.[ad?._id]?.trackingTemplate;
        const deepLink = reviewValues?.[ad?._id]?.deepLink;
        let data = [];

        if (ad.creatives.length == 1) {
          const creative = ad.creatives[0];
          // console.log("single creative ft:", creative.fileType);
            if (creative.fileType.startsWith("image/")) {
            updateStepProgress(
              "creative",
              createdCount,
              ads.length,
              `Processing single image creative for: ${ad.name}`
            );
            const imageHash = creative.facebookImageHash ? creative.facebookImageHash : creativeMap[creative._id];
            data = createSingleImageAdCreativePayload({
              name,
              bodies,
              titles,
              pageId: page_id,
              instagramActorId: instagram_actor_id,
              imageHash: imageHash,
              callToActionLink,
              callToActionType,
              trackingTemplate: trackingTemplate ? trackingTemplate : null,
              deepLink: deepLink ? deepLink : null,
            });
          } else if (creative.fileType.startsWith("video/")) {
            updateStepProgress(
              "creative",
              createdCount,
              ads.length,
              `Processing single video creative for: ${ad.name}`
            );
            const videoId = creative.facebookVideoId ? creative.facebookVideoId : creativeMap[creative._id];
            const thumbnailUrl = await handleVideoThumbnailUrl(videoId);
            data = createSingleVideoAdCreativePayload({
              name,
              bodies,
              titles,
              pageId: page_id,
              instagramActorId: instagram_actor_id,
              videoId: videoId,
              callToActionType,
              callToActionLink,
              thumbnailUrl,
              trackingTemplate: trackingTemplate ? trackingTemplate : null,
              deepLink: deepLink ? deepLink : null,
            });
          }
          console.log("single creative data:", data);
        }

        if (ad.creatives.length > 1) {
          const hasImageCreatives = ad.creatives.some((creative) => creative.fileType.startsWith("image/"));
          if (hasImageCreatives) {
            updateStepProgress(
              "creative",
              createdCount,
              ads.length,
              `Processing multiple image creatives for: ${ad.name}`
            );
            const creativesWithDuplicates = await handleGetDuplicateCreativeUrls(ad.creatives);
            data = createMultiPlacementImageAdCreativePayload({
              name,
              bodies,
              titles,
              pageId: page_id,
              instagramActorId: instagram_actor_id,
              creatives: creativesWithDuplicates,
              callToActionType,
              callToActionLink,
              trackingTemplate: trackingTemplate ? trackingTemplate : null,
              deepLink: deepLink ? deepLink : null
            });
          } else {
            updateStepProgress(
              "creative",
              createdCount,
              ads.length,
              `Processing multiple video creatives for: ${ad.name}`
            );
            const creativesWithThumbnails = await handleMultipleVideoThumbnailUrl(ad.creatives, creativeMap);
            data = createMultiPlacementVideoAdCreativePayload({
              name,
              bodies,
              titles,
              pageId: page_id,
              instagramActorId: instagram_actor_id,
              creatives: creativesWithThumbnails,
              callToActionType,
              callToActionLink,
              trackingTemplate: trackingTemplate ? trackingTemplate : null,
              deepLink: deepLink ? deepLink : null,
              creativeMapVideoIds: creativeMap,
            });
          }
        }

        const response = await handleCreateAdCreative(adAccountId, data, defaultValues);
        if (!response?.id) {
          throw new Error("Failed to create Facebook AdCreative");
        }
        adCreativeIdsMap[ad._id] = response.id;
        createdCount++;
        updateStepProgress(
          "creative",
          createdCount,
          ads.length,
          `Created ${createdCount}/${ads.length} ad creatives`
        );
        return Promise.resolve();
      } catch (error) {
        addStepError('creative', `Failed to create ad creative for "${ad.name}": ${error.message}`);
        throw { adName: ad.name, error: error.message };
      }
    });

    const results = await Promise.allSettled(newAdCreativePromises);
    const errors = results.filter((result) => result.status === "rejected");

    if (errors.length > 0 || Object.keys(adCreativeIdsMap).length !== ads.length) {
      updateStepProgress(
        "creative",
        createdCount,
        ads.length,
        `Failed: ${createdCount}/${ads.length} ad creatives created`
      );
      setStatus((prev) => {
        const newStatus = [...prev];
        newStatus[2] = "error";
        return newStatus;
      });
      setErrorStep(2);
      return;
    }

    updateStepProgress(
      "creative",
      ads.length,
      ads.length,
      "All ad creatives created successfully"
    );

    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[2] = "done";
      return newStatus;
    });

    // Step 4: Publishing Ads
    setStatus(prev => {
      const newStatus = [...prev];
      newStatus[3] = "in-progress";
      return newStatus;
    });

    updateStepProgress("publishing", 0, ads.length, "Starting to publish ads...");

    let postedAdIdsMap = {};
    let publishedCount = 0;

    const postAdsPromises = ads.map(async (ad) => {
      try {
        updateStepProgress(
          "publishing",
          publishedCount,
          ads.length,
          `Publishing ad: ${ad.name}`
        );

        const adCreativeId = adCreativeIdsMap[ad._id];
        if (!adCreativeId) {
          throw new Error(`Missing creative ID for ad: ${ad.name}`);
        }

        const response = await handlePostAd(
          adAccountId,
          adCreativeId,
          ad,
          defaultValues,
          handleCreateFacebookAd
        );

        if (!response?.id) {
          throw new Error("Failed to POST Facebook Ad");
        }

        postedAdIdsMap[ad._id] = response.id;
        publishedCount++;
        updateStepProgress(
          "publishing",
          publishedCount,
          ads.length,
          `Published ${publishedCount}/${ads.length} ads`
        );
        return Promise.resolve();
      } catch (error) {
        addStepError('publishing', `Failed to publish ad "${ad.name}": ${error.message}`);
        throw error;
      }
    });

    const adResults = await Promise.allSettled(postAdsPromises);
    const adErrors = adResults.filter((result) => result.status === "rejected");

    if (adErrors.length > 0 || Object.keys(postedAdIdsMap).length !== ads.length) {
      updateStepProgress(
        "publishing",
        publishedCount,
        ads.length,
        `Failed: ${publishedCount}/${ads.length} ads published`
      );
      setStatus((prev) => {
        const newStatus = [...prev];
        newStatus[3] = "error";
        return newStatus;
      });
      setErrorStep(3);
      return;
    }

    updateStepProgress(
      "publishing",
      ads.length,
      ads.length,
      "All ads published successfully"
    );

    setStatus((prev) => {
      const newStatus = [...prev];
      newStatus[3] = "done";
      return newStatus;
    });

    navigate("/ads-summary");
  };

  useEffect(() => {
    handleSaveAndNext();
  }, []);

  return (
    <>
      <div className="max-w-7xl mx-auto flex flex-col gap-4 p-6 bg-white min-h-screen">
        <Navbar title="Posting Bulk Ads on Meta" showBack={false} showSaveAndNext={false} />
        <div className="max-w-7xl mx-auto mt-10 p-6 bg-white shadow-lg rounded-xl">
          <h2 className="text-xl font-bold mb-4">Please Do NOT close this tab.</h2>
          <h3 className="text-s font-bold mb-4">(You can swtich tabs, do not close.)</h3>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.id} className="p-4 border rounded-lg flex flex-col gap-4 bg-gray-50 shadow-inner w-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{step.name}</p>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  <div>
                    {status[index] === "in-progress" && <Loader2 className="animate-spin text-blue-500" />}
                    {status[index] === "done" && <CheckCircle className="text-green-500" />}
                    {status[index] === "error" && <XCircle className="text-red-500" />}
                  </div>
                </div>

                {/* Progress Details */}
                {stepDetails[step.key].total > 0 && (
                  <div className="mt-2">
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          status[index] === "error" ? "bg-red-500" : "bg-blue-500"
                        }`}
                        style={{
                          width: `${(stepDetails[step.key].current / stepDetails[step.key].total) * 100}%`
                        }}
                      />
                    </div>
                    <p className="text-sm mt-1 text-gray-600">
                      {stepDetails[step.key].message || `Progress: ${stepDetails[step.key].current}/${stepDetails[step.key].total}`}
                    </p>
                  </div>
                )}

                {/* Error Messages */}
                {status[index] === "error" && stepErrors[step.key].length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md max-w-full">
                    <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                      {stepErrors[step.key].map((error, i) => (
                        <li key={i} className="break-words whitespace-normal leading-normal pl-2">
                          <span className="inline-block align-top">{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
